import { DIRECTION_VECTORS, DamageElement, EffectId, Vocation, } from "./constants.js";
import { ConditionId } from "./conditions.js";
/**
 * Catálogo de magias (PRD §9.3), **compartilhado** entre servidor e cliente.
 *
 * O cliente precisa de nome, custo e cooldown para montar a barra de hotkeys e mostrar
 * por que uma magia está apagada; o servidor precisa dos mesmos números para cobrar a
 * mana e recusar a conjuração. Uma tabela por lado divergiria no primeiro ajuste de
 * balanceamento, e o sintoma seria uma barra que diz "pronta" para uma magia que o
 * servidor ainda considera em recarga.
 */
/** Contra o que a magia é conjurada. */
export const SpellTarget = {
    /** No próprio conjurador. Cura e buff. */
    Self: 0,
    /** Numa criatura selecionada, dentro do alcance. */
    Creature: 1,
    /** Numa área ao redor do conjurador ou do alvo. */
    Area: 2,
};
/**
 * Formato da área (PRD §6). A geometria fica aqui, e não só no servidor, para que o
 * cliente possa desenhar a prévia da área com exatamente as mesmas casas que vão ser
 * atingidas — prévia que erra por um tile é pior que prévia nenhuma.
 */
export const AreaShape = {
    /** Um tile só: o alvo. */
    Single: 0,
    /** Círculo (na métrica de Chebyshev, um quadrado) centrado no alvo. */
    Circle: 1,
    /** Feixe reto partindo do conjurador na direção em que ele olha. */
    Beam: 2,
    /** Onda: leque que se abre à frente do conjurador. */
    Wave: 3,
};
const ALL_VOCATIONS = [
    Vocation.Knight,
    Vocation.Paladin,
    Vocation.Druid,
    Vocation.Sorcerer,
];
/**
 * Quatro magias por vocação (PRD §6), com identidade coerente com §9.1: o Knight segura
 * a criatura, o Paladin resolve à distância, o Druid cura e freia, o Sorcerer limpa
 * grupos.
 *
 * Os custos são lidos contra a mana inicial de cada vocação (§9.2): 20 no Knight, 100 no
 * Sorcerer. É por isso que a magia mais barata do Knight custa 10 e a do Sorcerer, 20 —
 * as duas cabem em duas conjurações no nível 1, apesar de os números serem diferentes.
 */
export const SPELLS = {
    // --- Knight ---
    1: {
        id: 1,
        name: 'Provocar',
        words: 'exeta res',
        vocations: [Vocation.Knight],
        minLevel: 1,
        manaCost: 10,
        cooldownMs: 6000,
        range: 3,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 3,
        taunt: true,
        effect: EffectId.Shout,
    },
    2: {
        id: 2,
        element: DamageElement.Physical,
        name: 'Grito de Guerra',
        words: 'exori vis',
        vocations: [Vocation.Knight],
        minLevel: 8,
        manaCost: 25,
        cooldownMs: 8000,
        range: 1,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 1,
        baseDamage: 20,
        damagePerMagicLevel: 3,
        effect: EffectId.Shout,
    },
    /**
     * O **segundo elemento do Knight**, e é por isso que ela existe (PRD §15.5).
     *
     * A regra é que toda vocação tenha dois elementos de dano na mão antes do nível 20 — sem
     * isso, a resistência elemental do bestiário é um muro que uma das quatro vocações não
     * tem como contornar, e o degrau 4 em diante deixa de ser jogável para ela. O Knight
     * tinha só físico: Grito de Guerra e Investida são músculo, e Provocar não fere.
     *
     * Lâmina em brasa, e não uma bola de fogo: o Knight continua resolvendo o combate
     * encostado na criatura (§9.1), e o alcance de 1 é o que mantém isso verdadeiro. O que
     * ela compra é o elemento, não a distância.
     */
    5: {
        id: 5,
        element: DamageElement.Fire,
        name: 'Lâmina em Brasa',
        words: 'exori flam',
        vocations: [Vocation.Knight],
        minLevel: 10,
        manaCost: 25,
        cooldownMs: 8000,
        range: 1,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 22,
        damagePerMagicLevel: 3,
        condition: { id: ConditionId.Fire, durationMs: 6000, damage: 4 },
        effect: EffectId.Fire,
    },
    3: {
        id: 3,
        element: DamageElement.Physical,
        name: 'Investida',
        words: 'exori hur',
        vocations: [Vocation.Knight],
        minLevel: 12,
        manaCost: 30,
        cooldownMs: 10000,
        range: 1,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 35,
        damagePerMagicLevel: 4,
        effect: EffectId.Charge,
    },
    4: {
        id: 4,
        name: 'Estancar Ferida',
        words: 'exura mas',
        vocations: [Vocation.Knight],
        minLevel: 5,
        manaCost: 20,
        cooldownMs: 8000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        baseHeal: 25,
        healPerMagicLevel: 3,
        effect: EffectId.Heal,
    },
    // --- Paladin ---
    10: {
        id: 10,
        element: DamageElement.Energy,
        name: 'Flecha Mágica',
        words: 'exori san',
        vocations: [Vocation.Paladin],
        minLevel: 1,
        manaCost: 15,
        cooldownMs: 2000,
        range: 6,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 15,
        damagePerMagicLevel: 5,
        effect: EffectId.Hit,
        projectile: EffectId.MagicArrow,
    },
    11: {
        id: 11,
        element: DamageElement.Physical,
        name: 'Salva de Projéteis',
        words: 'exevo con hur',
        vocations: [Vocation.Paladin],
        minLevel: 10,
        manaCost: 40,
        cooldownMs: 6000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Wave,
        radius: 4,
        baseDamage: 18,
        damagePerMagicLevel: 4,
        effect: EffectId.Hit,
        projectile: EffectId.Arrow,
    },
    13: {
        id: 13,
        element: DamageElement.Holy,
        name: 'Marca Sagrada',
        words: 'exori mas san',
        vocations: [Vocation.Paladin],
        minLevel: 14,
        manaCost: 55,
        cooldownMs: 8000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 1,
        baseDamage: 25,
        damagePerMagicLevel: 5,
        effect: EffectId.Energy,
        projectile: EffectId.MagicArrow,
    },
    // --- Druid ---
    /**
     * A versão do Druid da Cura Leve (id 12), e a razão de ele ser o único fora dela: dar as
     * duas à vocação que cura deixaria uma delas morta no catálogo. Mesmo custo, números
     * melhores — é a assinatura da vocação aparecendo na magia que todo mundo tem.
     *
     * Em **si mesmo**, como a Cura Leve que ela substitui. Quando ela mirava criatura, a
     * hotkey no meio da briga mirava o monstro selecionado no auto-attack — e `affect` recusa
     * curar criatura, então a conjuração saía, cobrava os vinte de mana e não curava ninguém.
     * Para curar o grupo o Druid continua tendo a Cura em Área e a runa de cura.
     */
    20: {
        id: 20,
        name: 'Cura',
        words: 'exura gran',
        vocations: [Vocation.Druid],
        minLevel: 1,
        manaCost: 20,
        cooldownMs: 2000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        baseHeal: 30,
        healPerMagicLevel: 8,
        effect: EffectId.Heal,
    },
    21: {
        id: 21,
        name: 'Cura em Área',
        words: 'exura san mas',
        vocations: [Vocation.Druid],
        minLevel: 12,
        manaCost: 60,
        cooldownMs: 6000,
        range: 0,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 2,
        baseHeal: 25,
        healPerMagicLevel: 6,
        effect: EffectId.Heal,
    },
    22: {
        id: 22,
        element: DamageElement.Ice,
        name: 'Onda de Gelo',
        words: 'exevo frigo hur',
        vocations: [Vocation.Druid],
        minLevel: 8,
        manaCost: 35,
        cooldownMs: 4000,
        range: 4,
        target: SpellTarget.Area,
        shape: AreaShape.Wave,
        radius: 3,
        baseDamage: 16,
        damagePerMagicLevel: 5,
        // Gelo atrasa em vez de prender: é o "frear o inimigo" de §9.1 sem tirar o controle
        // de ninguém. Quem foi atingido ainda decide se foge ou revida, só que mais devagar.
        condition: { id: ConditionId.Paralysis, durationMs: 5000, damage: 0 },
        effect: EffectId.Ice,
    },
    23: {
        id: 23,
        name: 'Raízes',
        words: 'exevo vis lux',
        vocations: [Vocation.Druid],
        minLevel: 6,
        manaCost: 30,
        cooldownMs: 12000,
        range: 5,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        rootMs: 4000,
        effect: EffectId.Roots,
    },
    /**
     * A magia que o Druid tem de dano **residual**, e a razão de ela existir é a curva de
     * §9.1: o Druid resolve o combate freando e curando, não somando dano por segundo. Um
     * veneno que cobra devagar cabe nesse papel de um jeito que uma segunda magia de impacto
     * não caberia — ele vale a pena contra o alvo que vai durar, que é justamente aquele em
     * que curar e frear já são a estratégia.
     */
    24: {
        id: 24,
        element: DamageElement.Earth,
        name: 'Nuvem Venenosa',
        words: 'exevo tox mas',
        vocations: [Vocation.Druid],
        minLevel: 10,
        manaCost: 45,
        cooldownMs: 8000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 2,
        baseDamage: 6,
        damagePerMagicLevel: 1,
        condition: { id: ConditionId.Poison, durationMs: 20000, damage: 6 },
        effect: EffectId.Poison,
    },
    // --- Sorcerer ---
    30: {
        id: 30,
        element: DamageElement.Fire,
        name: 'Bola de Fogo',
        words: 'exori flam',
        vocations: [Vocation.Sorcerer],
        minLevel: 1,
        manaCost: 20,
        cooldownMs: 2000,
        range: 6,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 18,
        damagePerMagicLevel: 6,
        condition: { id: ConditionId.Fire, durationMs: 6000, damage: 4 },
        effect: EffectId.Fire,
        projectile: EffectId.Fireball,
    },
    31: {
        id: 31,
        element: DamageElement.Energy,
        name: 'Explosão de Energia',
        words: 'exevo vis lux mas',
        vocations: [Vocation.Sorcerer],
        minLevel: 10,
        manaCost: 50,
        cooldownMs: 5000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 2,
        baseDamage: 22,
        damagePerMagicLevel: 6,
        effect: EffectId.Energy,
    },
    32: {
        id: 32,
        element: DamageElement.Fire,
        name: 'Onda de Fogo',
        words: 'exevo flam hur',
        vocations: [Vocation.Sorcerer],
        minLevel: 8,
        manaCost: 40,
        cooldownMs: 4000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Beam,
        radius: 5,
        baseDamage: 20,
        damagePerMagicLevel: 5,
        condition: { id: ConditionId.Fire, durationMs: 8000, damage: 5 },
        effect: EffectId.Fire,
    },
    33: {
        id: 33,
        element: DamageElement.Energy,
        name: 'Fúria',
        words: 'exori mort',
        vocations: [Vocation.Sorcerer],
        minLevel: 16,
        manaCost: 80,
        cooldownMs: 10000,
        range: 4,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 3,
        baseDamage: 30,
        damagePerMagicLevel: 8,
        effect: EffectId.Energy,
    },
    // --- Conjuração em item (PRD §6) ---
    /**
     * As magias que produzem objeto. Todas consomem runa em branco e todas exigem nível de
     * verdade: uma runa é dano ou cura que **atravessa a vocação** de quem a usa depois, e
     * fabricá-la cedo demais daria a qualquer personagem de nível 2 o arsenal de um conjurador
     * — bastaria comprar de quem fabrica.
     */
    25: {
        id: 25,
        name: 'Conjurar Runa de Cura',
        words: 'adura vita',
        vocations: [Vocation.Druid],
        minLevel: 12,
        manaCost: 90,
        cooldownMs: 4000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        conjures: { typeId: 82, count: 3 },
        consumes: { typeId: 80, count: 1 },
        effect: EffectId.Heal,
    },
    34: {
        id: 34,
        name: 'Conjurar Runa de Ataque',
        words: 'adori flam',
        vocations: [Vocation.Sorcerer],
        minLevel: 12,
        manaCost: 90,
        cooldownMs: 4000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        conjures: { typeId: 81, count: 3 },
        consumes: { typeId: 80, count: 1 },
        effect: EffectId.Fire,
    },
    35: {
        id: 35,
        name: 'Conjurar Runa de Explosão',
        words: 'adevo mas hur',
        vocations: [Vocation.Sorcerer],
        minLevel: 20,
        manaCost: 150,
        cooldownMs: 6000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        conjures: { typeId: 83, count: 2 },
        consumes: { typeId: 80, count: 1 },
        effect: EffectId.Energy,
    },
    /**
     * O Paladin conjura munição, e não runa.
     *
     * É a conjuração que cabe na vocação: §9.1 diz que ele resolve o combate ficando fora do
     * alcance, e o que o prende ao chão é a aljava acabando no meio da caçada. Dar-lhe runas
     * seria dar-lhe a solução de outra vocação; dar-lhe flechas resolve o problema que ele
     * tem. Não consome insumo pelo mesmo motivo — flecha já é o insumo.
     */
    14: {
        id: 14,
        name: 'Conjurar Flechas',
        words: 'adori con',
        vocations: [Vocation.Paladin],
        minLevel: 10,
        manaCost: 70,
        cooldownMs: 4000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        conjures: { typeId: 31, count: 50 },
        effect: EffectId.Energy,
    },
    // --- Efeitos de runa ---
    /**
     * Estas três não têm vocação, e a lista vazia é o mecanismo: `castSpell` recusa quem não
     * está nela, então nenhuma delas pode ser conjurada por hotkey ou por palavra. O único
     * caminho até elas é `useRune`, que entra por baixo dessa checagem porque quem pagou o
     * requisito foi quem fabricou o objeto.
     *
     * Os números são deliberadamente menores que os da magia equivalente da vocação: a runa é
     * dano portátil, que qualquer um usa e qualquer um vende, e igualá-la à magia tornaria a
     * vocação de conjurador um detalhe de fabricação.
     */
    50: {
        id: 50,
        element: DamageElement.Energy,
        name: 'Runa de ataque',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 1000,
        range: 6,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 14,
        damagePerMagicLevel: 3,
        effect: EffectId.Fire,
        projectile: EffectId.Fireball,
    },
    51: {
        id: 51,
        name: 'Runa de cura',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 1000,
        range: 5,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseHeal: 25,
        healPerMagicLevel: 4,
        effect: EffectId.Heal,
    },
    52: {
        id: 52,
        element: DamageElement.Fire,
        name: 'Runa de explosão',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 2000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 1,
        baseDamage: 16,
        damagePerMagicLevel: 3,
        effect: EffectId.Energy,
    },
    // As três runas de balcão (PRD §15.11).
    //
    // Entrada própria, e não um ponteiro para a Nuvem Venenosa do Druid ou para a Onda de Gelo:
    // a magia de vocação carrega `vocations` e `minLevel`, e uma runa que reaproveitasse a
    // entrada dela seria recusada na mão de quem não é da vocação — que é o oposto do que uma
    // runa é. A runa é magia **sem vocação**, e é isso que a torna mercadoria.
    //
    // Os números seguem a regra do trio de §6: abaixo da magia equivalente da vocação, porque
    // dano portátil que qualquer um compra não pode empatar com o ofício de quem estudou.
    53: {
        id: 53,
        element: DamageElement.Earth,
        name: 'Runa de veneno',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 2000,
        range: 5,
        target: SpellTarget.Area,
        shape: AreaShape.Circle,
        radius: 1,
        // Dano direto baixo e condição longa: é a runa que se joga **antes** da briga, e não
        // no meio dela. O veneno é o que ela vende.
        baseDamage: 8,
        damagePerMagicLevel: 1,
        condition: { id: ConditionId.Poison, durationMs: 18000, damage: 5 },
        effect: EffectId.Poison,
    },
    54: {
        id: 54,
        element: DamageElement.Ice,
        name: 'Runa de gelo',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 3000,
        range: 5,
        target: SpellTarget.Creature,
        shape: AreaShape.Single,
        radius: 0,
        baseDamage: 12,
        damagePerMagicLevel: 2,
        // Paralisia curta: é a única forma de **desengajar** que não depende de vocação, e o
        // preço de tê-la em objeto é ela durar menos que a Onda de Gelo do Druid (5s).
        condition: { id: ConditionId.Paralysis, durationMs: 3000, damage: 0 },
        effect: EffectId.Ice,
        projectile: EffectId.MagicArrow,
    },
    55: {
        id: 55,
        element: DamageElement.Energy,
        name: 'Runa de relâmpago',
        words: '',
        vocations: [],
        minLevel: 1,
        manaCost: 0,
        cooldownMs: 3000,
        range: 6,
        target: SpellTarget.Area,
        // Círculo de raio 2 contra o raio 1 da Runa de explosão, e não um feixe: `AreaShape.Beam`
        // parte da **direção em que o conjurador olha** (ver `spellArea`), e a runa é lançada por
        // clique num ponto. Um feixe que ignora o ponto clicado seria a única coisa do jogo em que
        // mirar não mira, e a recusa não teria como explicar isso.
        shape: AreaShape.Circle,
        radius: 2,
        baseDamage: 20,
        damagePerMagicLevel: 4,
        effect: EffectId.Energy,
    },
    // --- Comum a todas ---
    /**
     * A cura de todo mundo — menos do Druid, que no lugar dela tem a "Cura" (id 20), um pouco
     * mais forte. Antes dela, três vocações dependiam só da poção para repor vida, e a
     * auto-cura (PRD §6) prometia "a magia antes da poção" para quem não tinha magia nenhuma.
     *
     * O id 12 é herança de quando ela era só do Paladin. Fica: o id viaja no pacote `Cast` e
     * a lista de vocações já diz quem a conhece — renumerar não compraria nada.
     */
    12: {
        id: 12,
        name: 'Cura Leve',
        words: 'exura',
        vocations: [Vocation.Knight, Vocation.Paladin, Vocation.Sorcerer],
        minLevel: 1,
        manaCost: 20,
        cooldownMs: 3000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        baseHeal: 20,
        healPerMagicLevel: 5,
        effect: EffectId.Heal,
    },
    40: {
        id: 40,
        name: 'Luz',
        words: 'utevo lux',
        vocations: ALL_VOCATIONS,
        minLevel: 1,
        manaCost: 5,
        cooldownMs: 2000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        effect: EffectId.Shout,
    },
    /**
     * A magia de andar: +30% de velocidade por dez segundos, para as quatro vocações.
     *
     * O efeito inteiro mora na condição (`ConditionId.Haste`, o espelho benigno da paralisia)
     * — a magia só a aplica em quem conjurou, e é o motor de condições que dá a ela duração,
     * expiração e limpeza na morte de graça. Vai em `buff`, e não em `condition`, porque
     * acelerar a si mesmo não é agressão: em `condition`, a zona de proteção a recusaria.
     *
     * A recarga é mais curta que a duração de propósito: reconjurar antes de acabar renova os
     * dez segundos (`applyCondition` toma o maior), então manter-se rápido é uma escolha de
     * quanto de mana se queima por minuto, não um minigame de apertar no instante certo.
     */
    41: {
        id: 41,
        name: 'Agilidade',
        words: 'utani hur',
        vocations: ALL_VOCATIONS,
        minLevel: 6,
        manaCost: 30,
        cooldownMs: 2000,
        range: 0,
        target: SpellTarget.Self,
        shape: AreaShape.Single,
        radius: 0,
        buff: { id: ConditionId.Haste, durationMs: 10000, damage: 0 },
        effect: EffectId.Charge,
    },
};
export function spellById(id) {
    return SPELLS[id];
}
/** Magias que esta vocação pode conjurar, em ordem de nível exigido. */
export function spellsFor(vocation) {
    return Object.values(SPELLS)
        .filter((spell) => spell.vocations.includes(vocation))
        .sort((a, b) => a.minLevel - b.minLevel || a.id - b.id);
}
/**
 * Tiles atingidos pela magia.
 *
 * `originX/Y` é o conjurador; `targetX/Y`, o ponto mirado. Círculo é centrado no alvo,
 * feixe e onda partem do conjurador na direção em que ele olha — que é o que faz "onda
 * de fogo" ser uma decisão de posicionamento e não só um botão de dano.
 */
export function spellArea(spell, originX, originY, direction, targetX, targetY) {
    const tiles = [];
    switch (spell.shape) {
        case AreaShape.Single:
            tiles.push({ x: targetX, y: targetY });
            break;
        case AreaShape.Circle: {
            // Centro no alvo, exceto quando a magia é de alcance zero — aí o centro é quem
            // conjura, que é o caso da cura em área.
            const cx = spell.range === 0 ? originX : targetX;
            const cy = spell.range === 0 ? originY : targetY;
            for (let dy = -spell.radius; dy <= spell.radius; dy++) {
                for (let dx = -spell.radius; dx <= spell.radius; dx++) {
                    tiles.push({ x: cx + dx, y: cy + dy });
                }
            }
            break;
        }
        case AreaShape.Beam: {
            const vector = DIRECTION_VECTORS[direction];
            for (let step = 1; step <= spell.radius; step++) {
                tiles.push({ x: originX + vector.dx * step, y: originY + vector.dy * step });
            }
            break;
        }
        case AreaShape.Wave: {
            const vector = DIRECTION_VECTORS[direction];
            // Leque: a cada passo à frente, a largura cresce um tile para cada lado. Em
            // movimento cardeal, a perpendicular é a direção trocada de eixo.
            const sideX = vector.dy;
            const sideY = vector.dx;
            for (let step = 1; step <= spell.radius; step++) {
                const width = Math.floor(step / 2);
                for (let offset = -width; offset <= width; offset++) {
                    tiles.push({
                        x: originX + vector.dx * step + sideX * offset,
                        y: originY + vector.dy * step + sideY * offset,
                    });
                }
            }
            break;
        }
    }
    return tiles;
}
//# sourceMappingURL=spells.js.map